import {randomUUID} from 'Quoting/utils/utilities';
import {List as ImmutableList, Map as ImmutableMap} from 'immutable';

type ImmutableData = ImmutableMap<string, any> | ImmutableList<any>;

const updateKeyIfPresent = (
  map: ImmutableMap<string, any>,
  parentId: string | null
): {
  updatedMap: ImmutableMap<string, any>;
  newParentId: string | null;
  changed: boolean;
} => {
  const keyObj = map.get('_key');
  if (!ImmutableMap.isMap(keyObj)) {
    return {updatedMap: map, newParentId: parentId, changed: false};
  }

  const newId = randomUUID();

  const newKeyObj = keyObj.set('id', newId).set('parentId', parentId);

  const updatedMap = map.set('_key', newKeyObj);

  return {updatedMap, newParentId: newId, changed: true};
};

const updateList = (list: ImmutableList<any>, parentId: string | null): ImmutableList<any> => {
  let hasChanged = false;

  const newList = list.map((item: any) => {
    const updated = createPlanCopy(item, parentId);
    if (updated !== item) {
      hasChanged = true;
    }
    return updated;
  });

  return hasChanged ? newList : list;
};

const updateMap = (map: ImmutableMap<string, any>, parentId: string | null): ImmutableMap<string, any> => {
  let hasChanged = false;

  let currentObj = map;
  let currentParentId = parentId;

  const {updatedMap, newParentId, changed} = updateKeyIfPresent(currentObj, currentParentId);
  if (changed) {
    currentObj = updatedMap;
    hasChanged = true;
    currentParentId = newParentId;
  }

  const updatedEntries: [string, any][] = [];

  for (const [key, value] of currentObj.entries()) {
    const updatedValue = createPlanCopy(value, currentParentId);
    if (updatedValue !== value) {
      hasChanged = true;
    }
    updatedEntries.push([key, updatedValue]);
  }

  return hasChanged ? ImmutableMap<string, any>(updatedEntries) : map;
};

export const createPlanCopy = (planObj: ImmutableData, parentId: string | null): ImmutableData => {
  if (ImmutableList.isList(planObj)) {
    return updateList(planObj, parentId);
  }

  if (ImmutableMap.isMap(planObj)) {
    return updateMap(planObj, parentId);
  }

  return planObj;
};
